import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import { FC, ReactElement, useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ReactComponent as CheckListIcon } from '../../../assets/images/check-list-wrapper.svg';
import AlertDialog from '../../../components/dialog/AlertDialog';
import TitleGrid from '../../../components/grid/TitleGrid';
import BreadCrumb from '../../../components/title-page/BreadCrumb';
import TitlePage from '../../../components/title-page/TitlePage';
import { ChecklistTemplateContext } from '../../../context/checklistTemplateContext';
import { NotificationContext } from '../../../context/notificationContext';
import { initialState } from '../../../context/reducers/checklistTemplateReducer';
import { defaultPagination } from '../../../utils/constants/defaults';
import paths from '../../../utils/constants/paths';
import { ChecklistTemplateInterface } from '../../../utils/types/checklistTemplate';
import VesselEmptyListService from '../../vessels/VesselEmptyListService';
import TemplateBox from '../TemplateBox';
import getChecklistGridColumns from './ListGridColumns';
import './Checklist.scss';
import MrtDataTable from '../../../components/mrt-data-table';
import { Pagination } from '../../../utils/types/pagination';
import { MRT_PaginationState, MRT_SortingState } from 'material-react-table';

const defaultAttributesParams = ['id', 'name', 'createdAt', 'updatedAt'];
const defaultOrderParams = ['updatedAt:DESC'];
const defaultPaginationParams = {
  ...defaultPagination,
  attributes: defaultAttributesParams,
  join: ['creator', 'operator'],
  order: defaultOrderParams
};

const Checklist: FC = (): ReactElement => {
  const navigate = useNavigate();

  const { listChecklistTemplates, deleteChecklistTemplate, state } = useContext(ChecklistTemplateContext);
  const { showAlert } = useContext(NotificationContext);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedRow, setSelectedRow] = useState<ChecklistTemplateInterface>(initialState.view.data!);
  const [paginationParams, setPaginationParams] = useState<Pagination>(defaultPaginationParams);

  useEffect(() => {
    return () => {
      document.body.style.backgroundColor = '#FFF';
    };
  }, []);

  useEffect(() => {
    if (state.delete.done) {
      listChecklistTemplatesPaginated(paginationParams);

      showAlert({
        variant: 'success',
        title: 'Template',
        description: 'Template deleted!'
      });
    }

    if (state.delete.error) {
      showAlert({
        variant: 'error',
        title: 'Template',
        description: "Template couldn't be deleted!"
      });
    }
  }, [state.delete]);

  const onEditChecklist = (checklist: any) => {
    navigate({
      pathname: paths.TEMPLATE_CHECKLIST_FORM.absolutePath,
      search: `id=${checklist.id}`
    });
  };

  const onDeleteChecklist = (checklist: ChecklistTemplateInterface) => {
    setSelectedRow(checklist);
    setOpenDeleteDialog(true);
  };

  const handleDeleteChecklist = async () => {
    deleteChecklistTemplate(selectedRow.id);
    setOpenDeleteDialog(false);
  };

  const columns = getChecklistGridColumns(onEditChecklist, onDeleteChecklist);

  useEffect(() => {
    listChecklistTemplatesPaginated(paginationParams);
  }, [paginationParams]);

  const listChecklistTemplatesPaginated = useCallback((paginationParams: Pagination) => {
    listChecklistTemplates(paginationParams);
  }, [paginationParams]);

  const handlePaginationChange = (paginationState: MRT_PaginationState) => {
    setPaginationParams((prevParams) => {
      return {
        ...prevParams,
        page: paginationState.pageIndex + 1,
        perPage: paginationState.pageSize
      }
    });
  }

  const handleSortingChange = (sortingState: MRT_SortingState) => {
    const order = sortingState.length ?
      sortingState.map((sort) => `${sort.id}:${sort.desc ? 'DESC' : 'ASC'}`) :
      defaultOrderParams;

    setPaginationParams((prevParams) => {
      return {
        ...prevParams,
        order
      }
    });
  }

  return (
    <>
      <TitlePage
        leftContainer={<BreadCrumb previous={'Templates'} current={'Checklist'} />}
        rightContainer={
          <Button
            variant="outlined"
            onClick={() => navigate(paths.TEMPLATE_CHECKLIST_FORM.absolutePath)}
          >
            Create new template
          </Button>
        }
      />

      <Grid
        item
        xs={12}
        sm={12}
        md={12}
        lg={12}
        xl={12}
        style={{ marginTop: '71px', marginBottom: '49px' }}
      >
        <TemplateBox height={'calc(100vh - 240px)'}>
          {state.list.data?.results?.length === 0 ? (
            <VesselEmptyListService serviceName={'Checklist templates'} />
          ) : (
            <>
              <TitleGrid
                icon={<CheckListIcon className="icon-title" />}
                title={'Checklist templates'}
              />

              <MrtDataTable
                isLoading={state?.list.loading}
                isError={Boolean(state?.list.error)}
                columns={columns}
                rows={state?.list.data?.results}
                rowCount={state?.list.data?.pagination?.total}
                initialPagination={{
                  pageIndex: paginationParams.page - 1,
                  pageSize: paginationParams.perPage
                }}
                onPaginationChange={handlePaginationChange}
                onSortingChange={handleSortingChange}
              />
            </>
          )}
        </TemplateBox>
      </Grid>
      {openDeleteDialog && (
        <AlertDialog
          disableButtons={state.delete.loading}
          content={
            <Grid
              container
              direction="row"
              alignItems="center"
              margin={'auto'}
              justifyContent={'center'}
              textAlign={'center'}
            >
              <Grid item xs={10} sm={8.7} md={8.7} lg={8.7} xl={8.7}>
                <span style={modalTextStyle}>
                  Are you sure you want to remove <strong>{selectedRow.name}</strong>? There might
                  be vessels using this template
                </span>
              </Grid>
            </Grid>
          }
          cancelText={'No, cancel'}
          approveText={'Yes, remove'}
          open={openDeleteDialog}
          onClose={() => setOpenDeleteDialog(false)}
          onSave={handleDeleteChecklist}
        />
      )}
    </>
  );
};

export default Checklist;

const modalTextStyle = { color: '#1F1F1F', fontSize: '14px' };
